import {
  useRef,
  // useState,
  useEffect,
  useCallback,
  FC,
  PropsWithChildren,
  MouseEvent,
} from 'react'
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  useLazyQuery,
  gql,
  ApolloError,
  createHttpLink,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import dedent from 'dedent'
// import { useInView } from 'motion/react'
import * as demo from '../styles/Demo.styled'

const fetchToken = async (): Promise<string> => {
  const response = await fetch(`${process.env.SERVICE_URL}/api/auth`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      loginId: process.env.SERVICE_LOGIN_ID,
      password: process.env.SERVICE_PASSWORD,
    }),
  })

  const data = await response.json()

  return data.token
}

const httpLink = createHttpLink({
  uri: `${process.env.SERVICE_URL}/graphql`,
})

const authLink = setContext(async (_, { headers }) => {
  const token = await fetchToken()

  return {
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
    },
  }
})

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache({
    addTypename: false,
  }),
})

const GET_ORDERS = gql`
  query {
    orders {
      id
      name
      status
      customer {
        name
      }
    }
  }
`

const GET_CUSTOMERS = gql`
  query {
    customers {
      id
      name
      email
      phone
    }
  }
`

const CodeBlock: FC<
  PropsWithChildren<{
    delay?: number,
    duration?: number,
  }>
> = ({
  delay = 0,
  duration = 1,
  children,
}) => {
  const ref = useRef<HTMLPreElement>(null)
  // const inView = useInView(ref, { once: true, margin: '-20px' })
  // const [hasShown, setHasShown] = useState(false)

  // useEffect(() => {
  //   if (inView) setHasShown(true)
  // }, [inView])

  return (
    <demo.StyledCodeBlock
      ref={ref}
      initial={{
        opacity: 0,
        scale: 0,
      }}
      animate={{
        opacity: 1,
        scale: 1,
      }}
      // animate={
      //   hasShown
      //     ? { opacity: 1, scale: 1 }
      //     : { opacity: 0, scale: 0 }
      // }
      transition={{
        duration,
        ease: 'easeInOut',
        delay,
      }}
    >
      {children}
    </demo.StyledCodeBlock>
  )
}

const QueryDemo: FC<{
  dataName: string,
  query: typeof GET_ORDERS |
    typeof GET_CUSTOMERS,
  querySample: string,
}> = ({
  dataName,
  query,
  querySample,
}) => {
  const [
    getData, {
      loading,
      error,
      data,
    },
  ] = useLazyQuery(query, {})

  const resultRef = useRef<HTMLDivElement>(null)

  const isApolloError = useCallback((
    error: unknown
  ): error is ApolloError => {
    return error instanceof ApolloError
  }, [])

  const handleRunClick = useCallback((
    e: MouseEvent<HTMLButtonElement>
  ) => {
    (e.currentTarget as HTMLButtonElement).blur()

    getData()
  }, [getData])

  useEffect(() => {
    if (data && resultRef.current) {
      resultRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      })
    }
  }, [data])

  return (
    <>
      <CodeBlock delay={0.4}>
        {
          !data &&
            <demo.StyledRunButton
              onClick={handleRunClick}
            >
              run
            </demo.StyledRunButton>
        }
        <code>{querySample}</code>
      </CodeBlock>
      <h3>Query results:</h3>
      <div ref={resultRef}>
        {(() => {
          if (loading) {
            return (
              <>Loading...</>
            )
          } else if (error) {
            if (isApolloError(error)) {
              return (
                <>{error.message}</>
              )
            } else {
              return (
                <>{`Unknown error: ${String(error)}`}</>
              )
            }
          } else if (data) {
            return (
              <CodeBlock
                delay={0.1}
                duration={0.2}
              >
                <code>
                  {JSON.stringify(data[dataName], null, 2)}
                </code>
              </CodeBlock>
            )
          } else {
            return (
              <demo.StyledClickMessage
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 1,
                  ease: 'easeInOut',
                  delay: 1,
                }}
              >
                <>{`Click "run" above to fetch ${dataName}.`}</>
              </demo.StyledClickMessage>
            )
          }
        })()}
      </div>
    </>
  )
}

const Demo: FC = () => {
  return (
    <ApolloProvider client={client}>
      <h2>Here&apos;s a live demo of the server...</h2>
      <h3>Sample query for Orders:</h3>
      <QueryDemo
        dataName={'orders'}
        query={GET_ORDERS}
        querySample={dedent(`
          {
            orders {
              id
              name
              status
              customer {
                name
              }
            }
          }
        `)}
      />
      <h3>Sample query for Customers:</h3>
      <QueryDemo
        dataName={'customers'}
        query={GET_CUSTOMERS}
        querySample={dedent(`
          {
            customers {
              id
              name
              email
              phone
            }
          }
        `)}
      />
    </ApolloProvider>
  )
}

export default Demo
